import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { useTrackException } from 'presentation/events/hooks/useTrackException';

import { BTPayload } from './types';
import { useTrackBlueTrianglePageTurns } from './useTrackBlueTrianglePageTurns';
import { stripQueryString, writeBTPageTurn } from './utils';

const SOURCE = 'next';

export const useTrackBlueTriangle = () => {
	const router = useRouter();
	const trackException = useTrackException();
	const { trackStart, trackEnd } = useTrackBlueTrianglePageTurns();

	const activePayloadRef = useRef<BTPayload | null>(null);

	useEffect(() => {
		if (typeof window === 'undefined') return;

		try {
			writeBTPageTurn({ fromPath: stripQueryString(window.location.pathname), source: SOURCE });
		} catch (err) {
			trackException(err);
		}
	}, [trackException]);

	useEffect(() => {
		const handleStart = (url: string) => {
			try {
				const path = stripQueryString(url);

				if (activePayloadRef.current) {
					trackEnd(activePayloadRef.current);
					activePayloadRef.current = null;
				}

				activePayloadRef.current = trackStart(path);
			} catch (err) {
				console.error('[BT] routeChangeStart:', err);
				trackException(err);
			}
		};

		const handleComplete = (url: string) => {
			try {
				const path = stripQueryString(url);

				if (activePayloadRef.current) {
					trackEnd(activePayloadRef.current);
					activePayloadRef.current = null;
				}

				writeBTPageTurn({ fromPath: path, source: SOURCE });
			} catch (err) {
				console.error('[BT] routeChangeComplete:', err);
				trackException(err);
			}
		};

		const handleError = () => {
			activePayloadRef.current = null;
		};

		router.events.on('routeChangeStart', handleStart);
		router.events.on('routeChangeComplete', handleComplete);
		router.events.on('routeChangeError', handleError);

		return () => {
			router.events.off('routeChangeStart', handleStart);
			router.events.off('routeChangeComplete', handleComplete);
			router.events.off('routeChangeError', handleError);
		};
	}, [router.events, trackStart, trackEnd, trackException]);
};
